import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { postReviewStartAppeal, useAdminAppealsCache } from '../../../api/admin-appeals';
import { publishToast } from '../../../components/feedback';

/**
 * Review-start action hook (AUT-130). Direct TanStack mutation — the
 * backend `startReview` handler is role + permission + audit gated (no
 * money moves; AUT-138 removed per-action TOTP step-up project-wide).
 *
 * On success: invalidates the `['admin','appeals']` prefix (invalidate-
 * only; the response's `AppealDto` shape can't cross-patch the admin
 * detail cache) + success toast. The status transition (`pending →
 * under_review`) is re-derived from the refetched detail, which flips the
 * footer gating.
 *
 * On error: error toast. The server is the authority on the FSM gate — a
 * 409 from the wrong source state surfaces as the error toast (the footer
 * button is gated by `canReviewStart(status)` but the server stays the
 * source of truth).
 */
export const useReviewStart = (): UseMutationResult<void, Error, string> => {
  const { t } = useTranslation();
  const { invalidate } = useAdminAppealsCache();
  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await postReviewStartAppeal(id);
    },
    onSuccess: async () => {
      await invalidate();
      publishToast({
        severity: 'success',
        durationMs: 4000,
        message: t('appeals.reviewStart.successToast'),
      });
    },
    onError: () => {
      publishToast({
        severity: 'error',
        durationMs: 6000,
        message: t('appeals.reviewStart.errorToast'),
      });
    },
  });
};
